/**
 * Prose sanitation — docs/12 §2.
 *
 * Deterministic, idempotent, pure. Applied to every generated or revised span
 * before it is stored or displayed. Order matters, and running it twice must
 * equal running it once — a property the tests assert directly rather than
 * assume.
 *
 * No prompt achieves this reliably, which is the whole point: scaffolding must
 * never reach the reader, and "please don't include your reasoning" is a
 * request, not a guarantee.
 */

import { stringRatio } from './similarity';

/* ------------------------------------------------------------------ 1. reasoning */

const OPEN_TAGS = ['<think>', '<reasoning>'] as const;
const CLOSE_TAGS = ['</think>', '</reasoning>'] as const;
const OPEN_RE = /<(think|reasoning)\s*>/i;
const CLOSE_RE = /<\/(think|reasoning)\s*>/i;

/**
 * Remove reasoning blocks from a complete string.
 *
 * An unterminated block is removed through to the end, so that this agrees with
 * the streaming path below on truncated output. If they disagreed, a scene would
 * sanitise differently depending on whether it was streamed — which is exactly
 * the sort of difference nobody would think to look for.
 */
export function stripReasoningBlocks(text: string): string {
  let out = '';
  let rest = text;
  for (;;) {
    const open = OPEN_RE.exec(rest);
    if (!open) return out + rest;
    out += rest.slice(0, open.index);
    const after = rest.slice(open.index + open[0].length);
    const close = CLOSE_RE.exec(after);
    if (!close) return out; // unterminated: the remainder was all reasoning
    rest = after.slice(close.index + close[0].length);
  }
}

/** How much of `s`'s tail could still grow into one of `tags`. */
function partialTagTail(s: string, tags: readonly string[]): number {
  const max = Math.max(...tags.map((t) => t.length)) - 1;
  for (let n = Math.min(max, s.length); n > 0; n--) {
    const tail = s.slice(s.length - n).toLowerCase();
    if (tags.some((t) => t.startsWith(tail))) return n;
  }
  return 0;
}

/**
 * Streaming reasoning stripper.
 *
 * A separate state machine is not gold-plating: tags straddle chunk boundaries,
 * so a naive per-chunk regex lets `<thi` + `nk>` through intact and then emits
 * the entire reasoning block to the reader.
 */
export class ReasoningStripper {
  #buffer = '';
  #inside = false;
  /** True when the stream ended mid-block. */
  unterminated = false;

  push(chunk: string): string {
    this.#buffer += chunk;
    let emitted = '';

    for (;;) {
      if (this.#inside) {
        const close = CLOSE_RE.exec(this.#buffer);
        if (!close) {
          // Keep only what could still become a closing tag; the rest is
          // reasoning and is discarded rather than held in memory.
          const keep = partialTagTail(this.#buffer, CLOSE_TAGS);
          this.#buffer = this.#buffer.slice(this.#buffer.length - keep);
          return emitted;
        }
        this.#buffer = this.#buffer.slice(close.index + close[0].length);
        this.#inside = false;
        continue;
      }

      const open = OPEN_RE.exec(this.#buffer);
      if (open) {
        emitted += this.#buffer.slice(0, open.index);
        this.#buffer = this.#buffer.slice(open.index + open[0].length);
        this.#inside = true;
        continue;
      }

      // No complete opening tag. Emit everything except a tail that might yet
      // become one.
      const keep = partialTagTail(this.#buffer, OPEN_TAGS);
      emitted += this.#buffer.slice(0, this.#buffer.length - keep);
      this.#buffer = this.#buffer.slice(this.#buffer.length - keep);
      return emitted;
    }
  }

  /**
   * End of stream.
   *
   * Outside a block, the held tail was never a tag after all, so it is real
   * prose and gets flushed. Inside an unterminated block there is nothing to
   * flush: the content is reasoning, and doc 01's requirement that scaffolding
   * never reaches the reader outranks salvaging a truncated response. The
   * `unterminated` flag lets the caller say so rather than showing an
   * unexplained empty result.
   */
  flush(): string {
    if (this.#inside) {
      this.unterminated = true;
      this.#buffer = '';
      return '';
    }
    const out = this.#buffer;
    this.#buffer = '';
    return out;
  }
}

/* -------------------------------------------------------------------- 2. mojibake */

/**
 * UTF-8 bytes that were decoded as cp1252.
 *
 * The map is GENERATED by round-tripping rather than hardcoded: encode the
 * character as UTF-8, decode those bytes as windows-1252, and the result is
 * exactly the corrupted form to search for. A hand-written table drifts and is
 * silently incomplete; this cannot be either.
 */
function buildMojibakeMap(): [string, string][] {
  const interesting = [
    '—', '–', '‒', '―',           // dashes
    '“', '”', '‘', '’', '‚', // quotes
    '…', '•', ' ', '­',           // ellipsis, bullet, nbsp, shy
    'é', 'è', 'ê', 'ü', 'ö', 'ä',
    'á', 'í', 'ó', 'ú', 'ñ', 'ç',
    'É', 'Ü', 'Ö', 'Ä', 'Ñ', 'Ç',
    '™', '©', '®', '°', '½', '¼',
    '«', '»', '‹', '›', '′', '″',
  ];
  const enc = new TextEncoder();
  const dec = new TextDecoder('windows-1252');
  const pairs: [string, string][] = [];
  for (const ch of interesting) {
    const broken = dec.decode(enc.encode(ch));
    if (broken !== ch) pairs.push([broken, ch]);
  }
  // Longest first, so `â€œ` is repaired before the shorter `â€` prefix it
  // contains can claim part of it.
  pairs.sort((a, b) => b[0].length - a[0].length);
  return pairs;
}

const MOJIBAKE = buildMojibakeMap();

export function repairMojibake(text: string): string {
  let out = text;
  for (const [broken, good] of MOJIBAKE) {
    if (out.includes(broken)) out = out.split(broken).join(good);
  }
  // Two lossy variants the round-trip cannot produce, because the trailing
  // bytes were dropped rather than mis-decoded.
  out = out.replace(/(?<=\p{L})â(?=\p{L})/gu, '—'); // em dash, tail lost
  out = out.replace(/Â(?=\s)|(?<=\s)Â/g, '');            // stray from a nbsp
  return out;
}

/* ----------------------------------------------------------------- 3. scaffolding */

const SCENE_LABEL = /^\s*(?:#{1,6}\s*)?(?:\*\*|__)?\s*scene\s+\d+\s*[:.—-]?[^\n]*?(?:\*\*|__)?\s*$/i;

/**
 * Strip scene labels and a first line that merely restates the scene's summary.
 *
 * Both loop to a fixed point rather than acting once, which is what makes the
 * whole pipeline idempotent: an operation that removes "the first matching
 * line" is not a fixed point, and running it twice would eat a second line.
 */
export function stripScaffolding(text: string, summary?: string): string {
  const lines = text.split('\n');

  while (lines.length && (lines[0]!.trim() === '' || SCENE_LABEL.test(lines[0]!))) {
    if (lines[0]!.trim() === '' && !lines.slice(1).some((l) => SCENE_LABEL.test(l.trim()) && l.trim() !== '')) break;
    lines.shift();
  }

  if (summary && summary.trim()) {
    const target = summary.trim().toLowerCase();
    for (;;) {
      while (lines.length && lines[0]!.trim() === '') lines.shift();
      const first = lines[0]?.trim().toLowerCase();
      if (!first) break;
      const prefixEcho = first.length > 20 && target.length > 20
        && (first.startsWith(target) || target.startsWith(first));
      if (prefixEcho || stringRatio(first, target) >= 0.7) lines.shift();
      else break;
    }
  }

  return lines.join('\n');
}

/* ----------------------------------------------------------------- 4. punctuation */

const HORIZONTAL_RULE = /^\s*(?:-{3,}|\*{3,}|_{3,})\s*$/;

export function normalisePunctuation(text: string): string {
  const lines = text.split('\n').map((line) => {
    // Markdown horizontal rules are not em dashes waiting to happen.
    if (HORIZONTAL_RULE.test(line)) return line;
    let l = line.replace(/-{2,}/g, '—');
    l = l.replace(/[ \t]*—[ \t]*/g, '—');
    // A hyphen jammed against a capital or an opening quote is a model tic, not
    // a list item — a real list item has a space after the hyphen.
    l = l.replace(/^-(?=["'“‘\p{Lu}])/u, '');
    l = l.replace(/([.!?]\s+)-(?=["'“‘\p{Lu}])/gu, '$1');
    // KAELEN'S -> KAELEN's. Both apostrophes, because models emit the curly
    // one far more often than the straight one and a rule that only matched
    // U+0027 would almost never fire on real output.
    l = l.replace(/(\p{Lu}{2,})(['\u2019])S\b/gu, '$1$2s');
    return l;
  });
  return lines.join('\n');
}

/* ------------------------------------------------------------------ 5. whitespace */

export function normaliseWhitespace(text: string): string {
  return text
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .map((l) => l.replace(/[ \t]+$/, ''))
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/^\n+|\n+$/g, '');
}

/* -------------------------------------------------------------------- pipeline */

export interface SanitiseOptions {
  /** The scene's own summary, so an echo of it can be recognised and dropped. */
  summary?: string;
}

export function sanitise(text: string, options: SanitiseOptions = {}): string {
  let out = stripReasoningBlocks(text);
  out = repairMojibake(out);
  out = stripScaffolding(out, options.summary);
  out = normalisePunctuation(out);
  out = normaliseWhitespace(out);
  return out;
}
